/**
 * Tutorial / controls overlay — the "Esc menu": a centered panel of the game's
 * controls, toggled with Esc or the taskbar's OPT button. Carries the two live
 * actions: Resume (close) and Return to Title (back to the main menu).
 */

const SECTIONS: Array<{ title: string; rows: Array<[string, string]> }> = [
  {
    title: 'MOVE',
    rows: [
      ['WASD', 'walk (legs)'],
      ['Arrows', 'aim / look (torso)'],
      ['Shift', 'run'],
      ['Ctrl / C', 'crouch'],
    ],
  },
  {
    title: 'COMBAT · FOCUS',
    rows: [
      ['F', 'focus mode (auto-lock nearest / prime)'],
      ['G / V', 'cycle target left / right'],
      ['Space', 'fire'],
      ['1–4', 'weapon ♣ ♦ ♠ ♥'],
      ['M', 'mark focused target'],
      ['E', 'interact / open door / loot'],
    ],
  },
  {
    title: 'COVER (X to take cover)',
    rows: [
      ['A / D', 'slide along cover · peek the sides'],
      ['S', 'leave cover'],
      ['W', 'crawl over a half wall'],
      ['C', 'rise up / peek (stay locked)'],
    ],
  },
  {
    title: 'VIEW · GEAR',
    rows: [
      ['`', 'debug overlay + LOG/DEBUG/MOVE'],
      ['N', 'night vision'],
      ['I', 'inventory (or the INV button)'],
      ['Esc', 'this controls menu'],
      ['Wheel', 'zoom'],
      ['R', 'respawn (debug)'],
    ],
  },
];

export class TutorialOverlay {
  private readonly el: HTMLDivElement;
  private visible = false;

  /** `onReturnToTitle` fires when the player picks "Return to Title". */
  constructor(parent: HTMLElement, onReturnToTitle: () => void) {
    const el = document.createElement('div');
    el.className = 'tutorial-overlay';
    const cols = SECTIONS.map(
      (s) => `
        <div class="tut-col">
          <div class="tut-col-title">${s.title}</div>
          ${s.rows
            .map(([k, v]) => `<div class="tut-row"><span class="tut-key">${k}</span><span class="tut-desc">${v}</span></div>`)
            .join('')}
        </div>`,
    ).join('');
    el.innerHTML = `
      <div class="tut-card">
        <div class="tut-title">═══[ CONTROLS ]═══</div>
        <div class="tut-cols">${cols}</div>
        <div class="tut-actions">
          <button class="tut-btn tut-btn-resume" type="button">▸ Resume</button>
          <button class="tut-btn tut-btn-title" type="button">⏎ Return to Title</button>
        </div>
        <div class="tut-foot">Esc to close</div>
      </div>`;
    el.style.display = 'none';
    parent.appendChild(el);
    this.el = el;

    el.querySelector('.tut-btn-resume')?.addEventListener('click', () => this.hide());
    el.querySelector('.tut-btn-title')?.addEventListener('click', () => onReturnToTitle());
  }

  isVisible(): boolean {
    return this.visible;
  }

  show(): void {
    this.setVisible(true);
  }

  hide(): void {
    this.setVisible(false);
  }

  toggle(): void {
    this.setVisible(!this.visible);
  }

  setVisible(v: boolean): void {
    this.visible = v;
    this.el.style.display = v ? '' : 'none';
  }
}
